import Services from 'store/Services';
import State from 'store/State';
import { ThunkDispatch } from 'store/store';

import ParmUpdatesMessage from 'communication/types/parmUpdates/ParmUpdatesMessage';
import { StreamDataHandlers } from 'streamsState/StreamDataService';

import { StreamError } from 'streamsState/store/streamStatuses/StreamErrorState';
import { showNotificationAsync } from 'notification/store/thunks';
import { getStreamErrorMessageText } from 'streamsState/store/streamStatuses/actions';
import isNil from 'lodash/isNil';
import { OrderSide } from 'communication/types/order/OrderSide';
import { PortfolioPosition } from 'portfolio/PortfolioSelection';
import { OrderIdsSource } from 'embedded/store/EmbeddedAppState';
import { NotificationType } from '@instinet-analytics/semantic-ui-components';
import {
    embeddedAppStateActions,
    selectEmbeddedAppOrderIdsSource,
} from 'embedded/store/embeddedAppStateSlice';
import { getGlobalWindowVariables } from 'utils/globalWindowVariables';
import {
    NewportVersionDetails,
    setVersionDetails,
} from 'newport/store/newportAppSlice';
import { ApiRequestOptions } from 'communication/ApiCallInfo';

import { LoggingService } from '@instinet-analytics/core-lib';

export const parmUpdatesStreamKey = 'ParmUpdates';

export const streamParmUpdatesAction =
    (key: string) =>
    async (
        dispatch: ThunkDispatch<
            | ReturnType<typeof embeddedAppStateActions.setOrderIds>
            | ReturnType<typeof embeddedAppStateActions.setPositions>
            | ReturnType<typeof embeddedAppStateActions.clearPortfolio>
        >,
        getState: () => State,
        { streamDataService, loggingService }: Services
    ) => {
        const handlers: StreamDataHandlers<ParmUpdatesMessage> = {
            onMessage: (streamKey: string, data: ParmUpdatesMessage) => {
                const orderIdsSource = selectEmbeddedAppOrderIdsSource(
                    getState()
                );
                const action = buildUpdateEmbeddedAppStateAction(
                    data,
                    orderIdsSource,
                    loggingService
                );
                if (action) {
                    dispatch(action);
                }
            },
            onError: (streamKey: string, error: StreamError) => {
                dispatch(
                    showNotificationAsync(
                        getStreamErrorMessageText(error),
                        NotificationType.Warning
                    )
                );
            },
        };

        await streamDataService.startAndWatchStream(
            parmUpdatesStreamKey,
            'startParmUpdates',
            handlers
        )({
            key,
        });
    };

export function buildUpdateEmbeddedAppStateAction(
    data: ParmUpdatesMessage,
    orderIdsSource: OrderIdsSource | undefined,
    loggingService: LoggingService
):
    | ReturnType<typeof embeddedAppStateActions.setOrderIds>
    | ReturnType<typeof embeddedAppStateActions.setPositions>
    | ReturnType<typeof embeddedAppStateActions.clearPortfolio>
    | undefined {
    const orderIds = getOrderIds(data);
    const { port } = data;

    if (
        port === undefined &&
        orderIdsSource &&
        orderIdsSource !== 'UpdateParms'
    ) {
        loggingService.warn(
            'buildUpdateEmbeddedAppStateAction',
            `updateParms message disregarded as orderIdsSource is ${orderIdsSource}`,
            data
        );
        return undefined;
    }

    if (orderIds) {
        return embeddedAppStateActions.setOrderIds({
            orderIds,
            source: 'UpdateParms',
        });
    } else if (port !== undefined) {
        return embeddedAppStateActions.setPositions({
            values: getPositions(port),
        });
    } else {
        return embeddedAppStateActions.clearPortfolio();
    }
}

function getOrderIds(data: ParmUpdatesMessage): string[] | undefined {
    if (isNil(data._o)) {
        return undefined;
    }
    if (data._o.trim() === '') {
        return undefined;
    }
    return data._o.split(',');
}

function getPositions(port: string): PortfolioPosition[] {
    const entries = port.split('|');
    if (entries.length === 0) {
        return [];
    }

    const parts = entries[0].split(',');

    if (parts.length === 1) {
        return entries.map((e) => ({
            symbol: e,
            side: 'B',
            qty: 1,
            qtyRemaining: 1,
        }));
    } else if (parts.length === 2) {
        return entries.map((e) => {
            const ps = e.split(',');
            const qty = parseInt(ps[1]);
            const absQty = Math.abs(qty);
            return {
                symbol: ps[0],
                side: qty >= 0 ? 'B' : 'S',
                qty: absQty,
                qtyRemaining: absQty,
            };
        });
    } else if (parts.length === 3) {
        return entries.map((e) => {
            const ps = e.split(',');
            const qty = parseInt(ps[2]);
            return {
                symbol: ps[0],
                side: ps[1] as OrderSide,
                qty,
                qtyRemaining: qty,
            };
        });
    }

    return [];
}

export const fetchNewportVersionInfoThunk =
    () =>
    async (
        dispatch: ThunkDispatch<ReturnType<typeof setVersionDetails>>,
        getState: () => State,
        { indigoServiceClient, requestProgressService }: Services
    ) => {
        const application = getGlobalWindowVariables().application;
        const applicationVersion =
            getGlobalWindowVariables().applicationVersion;

        if (!application || !applicationVersion) {
            return;
        }

        const version = await requestProgressService.invokeAndMonitorRequest(
            'newportVersionDetails',
            (options: ApiRequestOptions) => {
                options.headers = {
                    ...options.headers,
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    ApplicationVersion: applicationVersion,
                };
                return indigoServiceClient.get<NewportVersionDetails>(
                    'env/newportVersions',
                    options
                );
            }
        );

        dispatch(setVersionDetails(version));
    };
